/**
 * Position names, and paths through the tree.
 *
 * Two different things, and conflating them was the mistake in the first
 * version of this file:
 *
 *  - A **`PositionName`** is one segment — `joint_1`, `wheel_fl` — and it is
 *    named **relative to its parent**. A structure revision for an assembled
 *    type defines its position names once, and every instance of that type
 *    inherits them. That is what makes two arms on one robot free: they are the
 *    same type, so `joint_1` is defined once, not twice with a prefix.
 *
 *  - A **`Path`** is the dotted route from the root — `arm_left.joint_3` — and
 *    it is what telemetry is keyed by (§5) and what a failure report names. It
 *    is *derived* by walking the tree; it is never stored on a component,
 *    because the moment an arm moves to another robot every path beneath it
 *    changes while nothing about the parts did.
 *
 * **No position name is hardcoded anywhere in this package.** Which positions
 * exist is a structure revision, which is data an engineer enters. This file
 * knows the grammar and nothing else.
 */

export type PositionName = string & { readonly __brand: 'PositionName' }
export type Path = string & { readonly __brand: 'Path' }

/** Lowercase, starts with a letter. Deliberately narrow: this is a contract. */
const SEGMENT = /^[a-z][a-z0-9_]*$/
const MAX_SEGMENT = 32
/** A tree deeper than this is a modelling error, not a robot. */
const MAX_DEPTH = 8

export type ParsedPosition =
  | { readonly ok: true; readonly name: PositionName }
  | { readonly ok: false; readonly reason: string }

/**
 * A single position name.
 *
 * A dot is refused with its own message, because "joint_3" versus
 * "arm_left.joint_3" is exactly the confusion this split exists to prevent, and
 * someone will type the second one into a structure revision.
 */
export function parsePositionName(raw: string): ParsedPosition {
  const s = raw.trim()
  if (s.includes('.')) {
    return {
      ok: false,
      reason:
        'a position name is one segment relative to its parent — "joint_3", not "arm_left.joint_3". The dotted form is a path through the tree.',
    }
  }
  if (!SEGMENT.test(s)) {
    return {
      ok: false,
      reason: `"${s}" must start with a letter and hold only lowercase letters, digits and underscores`,
    }
  }
  if (s.length > MAX_SEGMENT) {
    return { ok: false, reason: `"${s}" is longer than ${MAX_SEGMENT} characters` }
  }
  return { ok: true, name: s as PositionName }
}

/** For literals in tests and fixtures, and for code that has already validated. */
export function positionName(raw: string): PositionName {
  const parsed = parsePositionName(raw)
  if (!parsed.ok) throw new Error(`invalid position name "${raw}": ${parsed.reason}`)
  return parsed.name
}

export function isPositionName(raw: string): boolean {
  return parsePositionName(raw).ok
}

export type ParsedPath =
  | { readonly ok: true; readonly path: Path; readonly segments: readonly PositionName[] }
  | { readonly ok: false; readonly reason: string }

/**
 * A dotted path from the root.
 *
 * The empty string is valid and means **the root itself** — §5 keys robot-level
 * counters that way, and rejecting it would make every ingest of a robot-level
 * counter fail.
 */
export function parsePath(raw: string): ParsedPath {
  const s = raw.trim()
  if (s === '') return { ok: true, path: '' as Path, segments: [] }

  const parts = s.split('.')
  if (parts.length > MAX_DEPTH) {
    return { ok: false, reason: `a path deeper than ${MAX_DEPTH} is a modelling error` }
  }
  const segments: PositionName[] = []
  for (const part of parts) {
    const parsed = parsePositionName(part)
    if (!parsed.ok) return { ok: false, reason: `in "${s}": ${parsed.reason}` }
    segments.push(parsed.name)
  }
  return { ok: true, path: s as Path, segments }
}

export function path(raw: string): Path {
  const parsed = parsePath(raw)
  if (!parsed.ok) throw new Error(`invalid path "${raw}": ${parsed.reason}`)
  return parsed.path
}

export function isPath(raw: string): boolean {
  return parsePath(raw).ok
}

/** Build a path from segments. `joinPath([])` is the root. */
export function joinPath(segments: readonly PositionName[]): Path {
  return segments.join('.') as Path
}

/** The path of a child, given its parent's path and its position in it. */
export function childPath(parent: Path, position: PositionName): Path {
  return (parent === '' ? position : `${parent}.${position}`) as Path
}

/** The parent's path, or null at the root. */
export function parentPath(p: Path): Path | null {
  if (p === '') return null
  const i = p.lastIndexOf('.')
  return (i === -1 ? '' : p.slice(0, i)) as Path
}

/** Every ancestor path, root first, excluding the path itself. */
export function ancestorPaths(p: Path): readonly Path[] {
  const out: Path[] = []
  let current = parentPath(p)
  while (current !== null) {
    out.unshift(current)
    current = parentPath(current)
  }
  return out
}
